import React, { useState } from 'react'

import BusinessIcon from '@mui/icons-material/Business'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import PersonIcon from '@mui/icons-material/Person'
import { Box, Checkbox, IconButton, Stack, Tooltip } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { UrlProvider } from 'urls'

import { StatusChip } from 'components/_base'
import DataChangeRequestFilters from 'components/data-change-requests/DataChangeRequestFilters'
import DataChangeRequestsUsersDialog from 'components/data-change-requests/DataChangeRequestsUsersDialog'
import DataChangeRequestTypeTabs from 'components/data-change-requests/DataChangeRequestTypeTabs'
import ProposedChanges from 'components/data-change-requests/ProposedChanges'
import FormToggle from 'components/input/FormToggle'
import { type ColumnConfig, Table } from 'components/table'
import {
  type DataChangeRequestType,
  INITIAL_DATA_CHANGE_REQUEST_FILTERS,
  requestStatus,
  requestType,
} from 'constants/data-change-requests'
import { withForm } from 'decorators/withForm'
import { useAddNotification } from 'hooks/useAddNotification'
import {
  useAcceptDataChangeRequests,
  useDataChangeRequests,
  useDataChangeRequestTypeCounts,
  useRejectDataChangeRequests,
} from 'hooks/useDataChangeRequests'
import { usePaginatedTableMeta } from 'hooks/usePaginatedTableMeta'
import { formatDate } from 'utils/date'

import { getProposedFieldLabels, getSelectedDataChangeRequetsIds } from '../../utils/data-change-requests'

const DATA_CHANGE_REQUESTS_FORM = 'dataChangeRequestCompanies'

const ROWS_PER_PAGE = 50
// Sized columns total ~1275, so this floor leaves Proposed changes ~285 and the wrapper scrolls rather than crushing it.
const TABLE_MIN_WIDTH = 1560

const DataChangeRequests = () => {
  const { setValue: change, reset } = useFormContext()
  const selectedDataChangeRequests = useWatch() as Record<string, any>

  const { meta, setPage, setRowsPerPage, setSort, setFilters } = usePaginatedTableMeta(
    {
      filters: INITIAL_DATA_CHANGE_REQUEST_FILTERS,
      rowsPerPage: ROWS_PER_PAGE,
    },
    'dataChangeRequests',
  )
  const { data, isLoading, error } = useDataChangeRequests(meta)
  const counts = useDataChangeRequestTypeCounts(meta.filters)
  const accept = useAcceptDataChangeRequests()
  const reject = useRejectDataChangeRequests()
  const navigate = useNavigate()
  const addNotification = useAddNotification()

  const [usersDialog, setUsersDialog] = useState<{ open: boolean; users: any[] }>({ open: false, users: [] })

  const items = data?.items ?? []
  const type = meta.filters.type as DataChangeRequestType
  // Accepting an ISP request flags the company as an internet provider for every customer.
  const allowBulkActions = type === 'lead'

  const selectedIds = getSelectedDataChangeRequetsIds(selectedDataChangeRequests)
  const selectedRequests = items.filter(({ id }) => selectedIds.includes(id))
  const pendingItems = items.filter(({ status }) => status === 'pending')
  const allPendingSelected = !!pendingItems.length && pendingItems.every(({ id }) => selectedIds.includes(id))

  const setPartialFilters = (values: Record<string, any>) => setFilters({ ...meta.filters, ...values })

  const changeAllSelectedStatus = (selected: any) =>
    Object.keys(selectedDataChangeRequests || {}).forEach((id: any) => change(id, selected))

  const handleTypeChange = (nextType: DataChangeRequestType) => {
    reset({})
    setPartialFilters({ type: nextType })
  }

  const handleSort = (property: any) => () => setSort(property)

  const getRowLink = (dataChangeRequest: any) =>
    UrlProvider.getUrl('dataChangeRequests.view', { id: dataChangeRequest.id })

  const handleAccept =
    ({ id }: any) =>
    async () => {
      await accept.mutateAsync([id])
      changeAllSelectedStatus(false)
    }

  const handleReject =
    ({ id }: any) =>
    async () => {
      await reject.mutateAsync([id])
      changeAllSelectedStatus(false)
    }

  const handleBulkAction = (action: any) => async () => {
    // A selected row can go non-pending under us, so re-check before acting on the batch.
    if (!selectedRequests.every(({ status }) => status === 'pending')) {
      addNotification('Only pending change requests can be updated in bulk', 'error')
      return
    }

    const ids = selectedRequests.map(({ id }) => id)
    action === 'accept' ? await accept.mutateAsync(ids) : await reject.mutateAsync(ids)

    changeAllSelectedStatus(false)
  }

  const handleSelectAll = () => pendingItems.forEach(({ id }) => change(String(id), !allPendingSelected))

  const renderActions = ({ status, id }: any) => {
    if (status !== 'pending') return null
    return (
      <Stack direction="row" sx={{ flexWrap: 'nowrap' }}>
        <Tooltip title="Accept this request">
          <IconButton
            aria-label="Accept"
            onClick={handleAccept({ id })}
            size="small"
            sx={{ color: 'success.light', '&:hover': { color: 'success.main' } }}>
            <CheckCircleOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reject this request">
          <IconButton
            aria-label="Reject"
            onClick={handleReject({ id })}
            size="small"
            sx={{ color: 'error.light', '&:hover': { color: 'error.main' } }}>
            <CancelOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    )
  }

  const renderInfo = ({ uuid, usersReporting: users }: any) => {
    const handleUsersClick = (e: any) => {
      e.preventDefault()
      if (users.length > 1) {
        setUsersDialog({ open: true, users })
      } else {
        navigate(UrlProvider.getUrl('user', { id: users[0]?.userId }))
      }
    }

    const handleCompanyClick = (e: any) => {
      e.preventDefault()
      navigate(UrlProvider.getUrl('company', { uuid }))
    }

    return (
      <Stack direction="row" sx={{ flexWrap: 'nowrap' }}>
        <a href={`/companies/${uuid}`} onClick={handleCompanyClick} rel="noopener noreferrer" target="_blank">
          <IconButton aria-label="Companies" size="small">
            <BusinessIcon />
          </IconButton>
        </a>
        <a href={`/users/${users[0]?.userId}`} onClick={handleUsersClick} rel="noopener noreferrer" target="_blank">
          <IconButton aria-label="Users" size="small">
            <PersonIcon />
          </IconButton>
        </a>
      </Stack>
    )
  }

  const selectAllCheckbox = (
    <Checkbox
      checked={allPendingSelected}
      disabled={!pendingItems.length}
      indeterminate={!!selectedRequests.length && !allPendingSelected}
      onChange={handleSelectAll}
      slotProps={{ input: { 'aria-label': 'Select all' } }}
    />
  )

  // A disabled button emits no mouse events, so the span is what the tooltip listens on.
  const bulkTooltip = (action: string) =>
    selectedRequests.length
      ? `${action} the ${selectedRequests.length} selected request${selectedRequests.length === 1 ? '' : 's'}`
      : `Select rows to ${action.toLowerCase()} in bulk`

  const bulkActionButtons = (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      <Box component="span" sx={{ color: selectedRequests.length ? 'primary.main' : 'inherit' }}>
        {selectedRequests.length ? `${selectedRequests.length} selected` : 'Actions'}
      </Box>
      <Tooltip title={bulkTooltip('Accept')}>
        <span>
          <IconButton
            aria-label="Accept bulk"
            color="success"
            disabled={!selectedRequests.length}
            onClick={handleBulkAction('accept')}
            size="small">
            <DoneAllIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={bulkTooltip('Reject')}>
        <span>
          <IconButton
            aria-label="Reject bulk"
            color="error"
            disabled={!selectedRequests.length}
            onClick={handleBulkAction('reject')}
            size="small">
            <CancelPresentationIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  )

  const selectColumn: ColumnConfig = {
    title: selectAllCheckbox,
    headerStyle: { width: 52, paddingLeft: 4 },
    style: { paddingLeft: 4 },
    noRowClick: true,
    render: ({ status, id }: any) => (status === 'pending' ? <FormToggle name={String(id)} /> : null),
  }

  // Tight against the checkbox when there is one; without it Id leads the row and needs the normal cell padding.
  const idPadding = allowBulkActions ? { paddingLeft: 4 } : {}

  const config: ColumnConfig[] = [
    ...(allowBulkActions ? [selectColumn] : []),
    { title: 'Id', field: 'id', headerStyle: { width: allowBulkActions ? 48 : 64, ...idPadding }, style: idPadding },
    { title: 'Company UUID', field: 'uuid', sort: 'company_uuid', noBreak: true, headerStyle: { width: 250 } },
    {
      // The only unsized column, so it takes the remainder and tracks the viewport.
      title: 'Proposed changes',
      render: ({ data: proposed }: any) => {
        const labels = getProposedFieldLabels(proposed)
        return labels.length ? <ProposedChanges labels={labels} /> : null
      },
    },
    {
      title: 'Type',
      sort: 'type',
      headerStyle: { width: 120 },
      render: ({ type: rowType }: any) => <StatusChip {...requestType(rowType)} />,
    },
    {
      title: 'Status',
      sort: 'status',
      headerStyle: { width: 100 },
      render: ({ status }: any) => <StatusChip {...requestStatus(status)} />,
    },
    { title: 'Reported', field: 'numberOfReports', headerStyle: { width: 70 } },
    {
      title: 'Created',
      noBreak: true,
      headerStyle: { width: 140 },
      render: ({ createdAt }: any) => formatDate(createdAt),
    },
    {
      title: 'Updated',
      noBreak: true,
      headerStyle: { width: 140 },
      render: ({ updatedAt }: any) => formatDate(updatedAt),
    },
    { title: 'Info', noRowClick: true, headerStyle: { width: 90 }, render: renderInfo },
    {
      title: allowBulkActions ? bulkActionButtons : 'Actions',
      noRowClick: true,
      headerStyle: { width: 155 },
      render: renderActions,
    },
  ]

  return (
    <div className="campaigns-monitoring">
      <DataChangeRequestTypeTabs
        awaitingReview={meta.filters.status === 'pending'}
        counts={counts}
        onChange={handleTypeChange}
        value={type}
      />
      <Table
        className="campaigns-monitoring__table"
        config={config}
        count={data?.count}
        data={items}
        error={error ? 'Error occurred when loading data change requests data' : undefined}
        getRowLink={getRowLink}
        hover
        loading={isLoading}
        minWidth={TABLE_MIN_WIDTH}
        onChangePage={setPage}
        onChangeRowsPerPage={setRowsPerPage}
        onSort={handleSort}
        page={meta.page}
        rowsPerPage={meta.rowsPerPage}
        sortBy={meta.sortBy}
        sortOrder={meta.sortOrder}
        title="Data&nbsp;Change&nbsp;Requests"
        toolbar={<DataChangeRequestFilters initialValues={meta.filters} setFilters={setPartialFilters} />}
      />
      <DataChangeRequestsUsersDialog
        onClose={() => setUsersDialog(s => ({ ...s, open: false }))}
        open={usersDialog.open}
        users={usersDialog.users}
      />
    </div>
  )
}

export default withForm({
  form: DATA_CHANGE_REQUESTS_FORM,
})(DataChangeRequests)
